/* =====================================================================
 * @auw/i18n — static resource registry.
 *
 * Aggregates every locale × namespace JSON into a single `RESOURCES`
 * constant so the bootstrap module can hand it to `i18next.init()`
 * without a dynamic import graph at runtime. Each bundle holds 35
 * small JSON files (5 locales × 7 namespaces) — Vite / Rollup tree-
 * shake the unused locale keys if `supportedLngs` ever narrows.
 *
 * Every string that mentions the product brand uses the
 * `{{productName}}` interpolation placeholder. The postProcessor registered in
 * `./index.ts` fills it from the active tenant manifest so the same
 * bundle renders `askyour.ai` for B2C and a tenant-specific brand
 * (SOLA / Sungwoo AI / …) for on-prem deployments.
 * ===================================================================== */

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enSettings from './locales/en/settings.json';
import enWorkspace from './locales/en/workspace.json';
import enAdmin from './locales/en/admin.json';
import enWidget from './locales/en/widget.json';
import enTools from './locales/en/tools.json';

import koCommon from './locales/ko/common.json';
import koAuth from './locales/ko/auth.json';
import koSettings from './locales/ko/settings.json';
import koWorkspace from './locales/ko/workspace.json';
import koAdmin from './locales/ko/admin.json';
import koWidget from './locales/ko/widget.json';
import koTools from './locales/ko/tools.json';

import jaCommon from './locales/ja/common.json';
import jaAuth from './locales/ja/auth.json';
import jaSettings from './locales/ja/settings.json';
import jaWorkspace from './locales/ja/workspace.json';
import jaAdmin from './locales/ja/admin.json';
import jaWidget from './locales/ja/widget.json';
import jaTools from './locales/ja/tools.json';

import zhCNCommon from './locales/zh-CN/common.json';
import zhCNAuth from './locales/zh-CN/auth.json';
import zhCNSettings from './locales/zh-CN/settings.json';
import zhCNWorkspace from './locales/zh-CN/workspace.json';
import zhCNAdmin from './locales/zh-CN/admin.json';
import zhCNWidget from './locales/zh-CN/widget.json';
import zhCNTools from './locales/zh-CN/tools.json';

import zhTWCommon from './locales/zh-TW/common.json';
import zhTWAuth from './locales/zh-TW/auth.json';
import zhTWSettings from './locales/zh-TW/settings.json';
import zhTWWorkspace from './locales/zh-TW/workspace.json';
import zhTWAdmin from './locales/zh-TW/admin.json';
import zhTWWidget from './locales/zh-TW/widget.json';
import zhTWTools from './locales/zh-TW/tools.json';

export const NAMESPACES = [
  'common',
  'auth',
  'settings',
  'workspace',
  'admin',
  'widget',
  'tools',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const RESOURCES = {
  en: {
    common: enCommon,
    auth: enAuth,
    settings: enSettings,
    workspace: enWorkspace,
    admin: enAdmin,
    widget: enWidget,
    tools: enTools,
  },
  ko: {
    common: koCommon,
    auth: koAuth,
    settings: koSettings,
    workspace: koWorkspace,
    admin: koAdmin,
    widget: koWidget,
    tools: koTools,
  },
  ja: {
    common: jaCommon,
    auth: jaAuth,
    settings: jaSettings,
    workspace: jaWorkspace,
    admin: jaAdmin,
    widget: jaWidget,
    tools: jaTools,
  },
  'zh-CN': {
    common: zhCNCommon,
    auth: zhCNAuth,
    settings: zhCNSettings,
    workspace: zhCNWorkspace,
    admin: zhCNAdmin,
    widget: zhCNWidget,
    tools: zhCNTools,
  },
  'zh-TW': {
    common: zhTWCommon,
    auth: zhTWAuth,
    settings: zhTWSettings,
    workspace: zhTWWorkspace,
    admin: zhTWAdmin,
    widget: zhTWWidget,
    tools: zhTWTools,
  },
} as const;
